import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { cityBySlug, CITIES } from '../data/cities';
import { CITY_I18N, CITY_COPY, CITY_SEO, CITY_NAMES } from '../data/cityI18n';
import { usePricing } from '../lib/pricing';
import { useLang } from '../i18n/LanguageContext';
import { ArrowRight, MapPin, Clock, Sparkles, MessageCircle } from 'lucide-react';

const SITE = 'https://www.party-led.com';

const CityPage = ({ citySlug }) => {
  const city = cityBySlug(citySlug);
  const { lang, switchLang } = useLang();
  const isAE = city?.country === 'AE';
  const pricing = usePricing(lang, isAE ? 'AE' : undefined);

  // Sur les pages des Émirats, on ouvre dans la langue du navigateur (EN/AR)
  // tant que le visiteur n'a pas choisi de langue lui-même.
  useEffect(() => {
    if (!isAE) return;
    if (localStorage.getItem('partyledLang')) return;
    const nav = (navigator.language || '').toLowerCase();
    if (nav.startsWith('ar')) switchLang('ar');
    else if (nav.startsWith('en')) switchLang('en');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAE]);

  if (!city) {
    return (
      <div className="min-h-screen bg-[#0b0b0c] flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-gray-400 mb-6">Cette ville n'est pas encore couverte par une page dédiée.</p>
          <Link to="/devis"><Button className="bg-purple-600 hover:bg-purple-500 rounded-full px-8">Demander un devis</Button></Link>
        </div>
      </div>
    );
  }

  const c = CITY_COPY[lang] || CITY_COPY.fr;
  const displayName = CITY_NAMES[city.slug]?.[lang] || city.name;
  const r = (s) => (s || '').replaceAll('{city}', displayName);
  const loc = CITY_I18N[city.slug]?.[lang];
  const region = loc?.region || city.region;
  const intro = loc?.intro || city.intro;
  const venues = loc?.venues || city.venues;
  const isRTL = lang === 'ar';

  const url = `${SITE}/bracelets-led-${city.slug}`;
  let title = isAE
    ? `Bracelets LED ${city.name} (Émirats) — animation lumineuse pour événements | Party LED`
    : `Bracelets LED ${city.name} — location et vente pour événements | Party LED`;
  let description = isAE
    ? `Animation lumineuse à ${city.name} (Émirats arabes unis) : kits de bracelets LED synchronisés de 50 à 1 000 personnes. Marquage 1 couleur offert, devis via notre revendeur local.`
    : `Animation lumineuse à ${city.name} (${city.region}) : kits de bracelets LED synchronisés de 50 à 1 000 personnes, livrés prêts à l'emploi. Marquage 1 couleur offert, devis en 24 h.`;
  if (CITY_SEO[lang]) {
    title = r(CITY_SEO[lang].title);
    description = r(CITY_SEO[lang].description);
  }

  const ogImage = `${SITE}/og/${city.slug}.jpg`;
  const canonical = (lang === 'en' || lang === 'ar') ? `${url}?lang=${lang}` : url;
  const waNumber = isAE ? '971585070177' : '33767735161';
  const waLink = `https://wa.me/${waNumber}?text=${encodeURIComponent(r(c.waMsg))}`;

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: `${SITE}/` },
      { '@type': 'ListItem', position: 2, name: 'Nos villes', item: `${SITE}/villes` },
      { '@type': 'ListItem', position: 3, name: `Bracelets LED à ${city.name}`, item: url },
    ],
  };
  const service = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Location et vente de bracelets LED à ${city.name}`,
    serviceType: 'Animation lumineuse par bracelets LED synchronisés',
    areaServed: { '@type': 'City', name: city.name },
    provider: { '@type': 'Organization', name: 'Party LED', url: SITE },
    description,
  };

  return (
    <div className="min-h-screen bg-[#0b0b0c] pt-28 pb-20 px-6" dir={isRTL ? 'rtl' : 'ltr'} data-testid={`city-page-${city.slug}`}>
      <>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="fr" href={url} />
        <link rel="alternate" hrefLang="en" href={`${url}?lang=en`} />
        <link rel="alternate" hrefLang="ar" href={`${url}?lang=ar`} />
        <link rel="alternate" hrefLang="x-default" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
        <script type="application/ld+json">{JSON.stringify(service)}</script>
      </>

      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="w-4 h-4 text-purple-400" />
          <p className="text-purple-400 text-xs tracking-[0.25em] uppercase">{region}</p>
        </div>

        <h1 className="text-white font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-6">{r(c.h1)}</h1>
        <p className="text-gray-300 text-base leading-relaxed mb-4">{intro}</p>
        <p className="text-gray-400 text-sm leading-relaxed mb-8">{c.sub}</p>

        {/* CTA contact visible */}
        <div className="flex flex-col sm:flex-row gap-3 mb-12">
          <Link to={`/devis?ville=${encodeURIComponent(city.name)}`} className="flex-1">
            <Button className="w-full bg-purple-600 hover:bg-purple-500 rounded-full py-6 text-base" data-testid={`city-cta-quote-${city.slug}`}>
              {c.ctaQuote}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
          <a href={waLink} target="_blank" rel="noopener noreferrer" className="flex-1">
            <Button className="w-full bg-[#25D366] hover:bg-[#1fb857] text-white rounded-full py-6 text-base" data-testid={`city-cta-whatsapp-${city.slug}`}>
              <MessageCircle className="mr-2 w-4 h-4" />
              {c.ctaWhatsapp}
            </Button>
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
          {[
            { icon: Sparkles, title: c.b1t, text: c.b1x },
            { icon: Clock, title: c.b2t, text: c.b2x },
            { icon: MapPin, title: r(c.b3t), text: isAE ? c.b3xAE : c.b3xFR },
          ].map((b) => (
            <div key={b.title} className="bg-white/[0.02] border border-white/10 rounded-xl p-5">
              <b.icon className="w-5 h-5 text-purple-400 mb-3" />
              <p className="text-white text-sm font-semibold mb-1">{b.title}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>

        <h2 className="text-white text-lg md:text-lg font-semibold mb-4">{r(c.venuesH2)}</h2>
        <ul className="space-y-3 mb-12">
          {venues.map((v) => (
            <li key={v} className="text-gray-400 text-sm flex gap-3">
              <span className="text-purple-400">—</span>
              <span className="first-letter:uppercase">{v}</span>
            </li>
          ))}
        </ul>

        <h2 className="text-white text-lg md:text-lg font-semibold mb-4">{c.includedH2}</h2>
        <ul className="space-y-2 mb-12 text-gray-400 text-sm">
          {c.included.map((it) => <li key={it}>{it}</li>)}
        </ul>

        <div className="bg-gradient-to-br from-purple-600/15 to-transparent border border-purple-500/25 rounded-2xl p-8 mb-12">
          <h2 className="text-white text-lg md:text-lg font-semibold mb-2">{r(c.quoteTitle)}</h2>
          <p className="text-gray-400 text-sm leading-relaxed mb-6">
            {c.quoteText}
            {isAE && c.aeNote}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to={`/devis?ville=${encodeURIComponent(city.name)}`}>
              <Button className="bg-purple-600 hover:bg-purple-500 rounded-full px-8 py-6" data-testid={`city-quote-${city.slug}`}>
                {c.ctaQuoteShort}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <a href={waLink} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="rounded-full px-8 py-6 border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10 hover:text-[#25D366]">
                <MessageCircle className="mr-2 w-4 h-4" />
                WhatsApp
              </Button>
            </a>
          </div>
        </div>

        <h2 className="text-white text-lg md:text-lg font-semibold mb-4">{c.kitsH2}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-12">
          {Object.entries(pricing.prices || {}).slice(0, 6).map(([s, p]) => (
            <Link key={s} to={`/produit/${s}`} className="bg-white/[0.02] border border-white/10 rounded-xl p-4 hover:border-purple-500/40 transition-colors">
              <p className="text-white text-sm font-medium">{s.replace('party-led-kit-', 'Kit ').replace('-personnes', ' pers.')}</p>
              <p className="text-gray-400 text-xs mt-1">{pricing.fmt(p.priceHT)} {c.ht}</p>
            </Link>
          ))}
        </div>

        <h2 className="text-white text-lg md:text-lg font-semibold mb-4">{c.otherCitiesH2}</h2>
        <div className="flex flex-wrap gap-2">
          {CITIES.filter((x) => x.slug !== city.slug && x.country === city.country).map((x) => (
            <Link key={x.slug} to={`/bracelets-led-${x.slug}`}
                  className="px-4 py-2 rounded-full border border-white/10 text-gray-400 text-xs hover:text-white hover:border-purple-500/40 transition-colors">
              {x.name}
            </Link>
          ))}
          <Link to="/villes"
                className="px-4 py-2 rounded-full border border-purple-500/30 text-purple-400 text-xs hover:bg-purple-600/10 transition-colors">
            {c.allCities}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CityPage;
